import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { HTMLRange } from '../types';
import { TOCNode } from '../../../types/DocumentTypes';
import { useDocumentContext } from '../../../components/DocumentContext';
import { useUserRelatedDocumentContext } from '../../../components/DocumentUserRelatedContext';

import { getHTMLRangeIndex } from '../utils/htmlRangeUtils';
import { findNextRootChapter, findRootChapter } from '../utils/chapterUtils';
import { useUserManagement } from '../../UserManagement/components/UserManagementContext';
import { useSearchParams } from 'react-router';

type ReaderState = 'loading' | 'ready' | 'error';

interface ReaderContextValue {
  state: ReaderState;
  currentChapter: TOCNode | null; // Chapter user has navigated to
  loadedSection: TOCNode | null; // Chapter which content should be displayed
  nextChapter: TOCNode | null; // Next loadable chapter
  previousChapter: TOCNode | null; // Previous loadable chapter
  htmlRange: HTMLRange | null; // HTML range of the current chapter
  moveToChapter: (chapter: TOCNode) => Promise<void>;
}

const ReaderContext = createContext<ReaderContextValue>({
  state: 'loading',
  currentChapter: null,
  loadedSection: null,
  nextChapter: null,
  previousChapter: null,
  htmlRange: null,
  moveToChapter: async () => {},
});

export function ReaderContextProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const { chapters, id: documentId } = useDocumentContext();
  const { identity } = useUserManagement();
  const { setMoveToChapter, initReadingSession, trackProgress } =
    useUserRelatedDocumentContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<ReaderState>('loading');
  const [currentChapter, setCurrentChapter] = useState<TOCNode | null>(null);
  const [loadedSection, setLoadedSection] = useState<TOCNode | null>(null);
  const [htmlRange, setHtmlRange] = useState<HTMLRange | null>(null);
  const didInitRef = useRef(false);
  const didFirstNavRef = useRef(false);

  const nextChapter = useMemo(() => {
    return findNextRootChapter(currentChapter, chapters, 'next');
  }, [currentChapter, chapters]);

  const previousChapter = useMemo(() => {
    return findNextRootChapter(currentChapter, chapters, 'previous');
  }, [currentChapter, chapters]);

  const loadChapter = useCallback(
    async (chapter: TOCNode) => {
      return getHTMLRangeIndex(
        documentId,
        chapter.blockId,
        await identity?.actions?.getAccessToken(),
      );
    },
    [documentId, identity],
  );

  const moveToChapter = useCallback(
    async (chapter: TOCNode) => {
      const rootChapter = findRootChapter(chapter, chapters as TOCNode[]);

      if (!rootChapter) {
        setState('error');
        return;
      }

      // Check if we need to load new content
      const needsNewContent =
        !currentChapter ||
        rootChapter?.nodeId !==
          findRootChapter(currentChapter, chapters as TOCNode[])?.nodeId;

      if (needsNewContent) {
        setState('loading');
        try {
          const range = await loadChapter(rootChapter);
          setHtmlRange(range);
          // Ensure we scroll to top after loading new chapter content, but skip on first navigation to avoid flash
          if (didFirstNavRef.current) {
            window.scrollTo({ top: 0, behavior: 'auto' });
          }
        } catch (_error) {
          setState('error');
        }
      }

      setCurrentChapter(chapter);
      setLoadedSection(rootChapter);
      if (!didFirstNavRef.current) {
        didFirstNavRef.current = true;
      }

      // Update URL with chapter navigation
      if (searchParams.get('chapterId') !== chapter.nodeId.toString()) {
        setSearchParams((prev) => {
          prev.set('chapterId', chapter.nodeId.toString());
          return prev;
        });
      }

      setState('ready');
    },
    [currentChapter, chapters, searchParams, setSearchParams, loadChapter],
  );

  // Register moveToChapter with DocumentUserRelatedContext for continue reading functionality
  useEffect(() => {
    if (setMoveToChapter) {
      setMoveToChapter(moveToChapter);
    }
  }, [setMoveToChapter, moveToChapter]);

  // Simple initialization - let ProgressManager handle all the complex stuff
  useEffect(() => {
    if (didInitRef.current || state !== 'loading' || !chapters?.length) {
      return;
    }

    const initializeReader = async () => {
      try {
        const result = await initReadingSession(chapters, searchParams);

        // Just navigate to the target - ProgressManager handles all business logic
        await moveToChapter(result.location.chapter);
        // If no modal (decision already true), start tracking now
        if (result.action === 'navigate') {
          // Defer to next tick to ensure content is mounted
          setTimeout(() => {
            trackProgress();
          }, 0);
        }
      } catch (_error) {
        setState('error');
      }
    };

    initializeReader();
    didInitRef.current = true;
  }, [state, chapters, searchParams, moveToChapter, initReadingSession]); // eslint-disable-line react-hooks/exhaustive-deps

  const contextValue: ReaderContextValue = useMemo(
    () => ({
      state,
      currentChapter,
      loadedSection,
      nextChapter,
      previousChapter,
      htmlRange,
      moveToChapter,
    }),
    [
      state,
      currentChapter,
      loadedSection,
      nextChapter,
      previousChapter,
      htmlRange,
      moveToChapter,
    ],
  );

  return (
    <ReaderContext.Provider value={contextValue}>
      {children}
    </ReaderContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReaderContext() {
  const context = useContext(ReaderContext);
  if (context === undefined) {
    throw new Error(
      'useReaderContext must be used within a ReaderContextProvider',
    );
  }
  return context;
}
